import { createAsyncThunk } from "@reduxjs/toolkit"
import wsClient from "src/wsClient"
import i18n from "src/i18n"

import {
  addChatMessage,
  addReaction,
  selectCurrentTeam,
  selectGamePlayers,
  selectLiveGame,
  setUserReady,
  setGame,
  setRoundTrail,
  setRoundMotion,
  setPlayerPresence,
} from "src/store/game"
import { selectUserId, selectUsername } from "src/store/user"
import { addMessage } from "src/store/messages"
import { isSilentRefusal, playerFacingError } from "src/utils/errorMessages"
import { describeTrade } from "src/utils/tradeWording"
import { loadFogPreview, subscribeFogPreview } from "src/utils/fogPreview"
import { playUiSound } from "src/utils/audio"
import { withOnlyMyEvents } from "src/utils/ownEvents"
import { motionHasClash } from "src/utils/roundMotion"
import { thunk as getGames } from "./getGames"

// Flipping the preview while a game socket is open tells the server at once; with no socket open
// `send` just reports false and the next connect says it. Subscribed once, at module level.
subscribeFogPreview((enabled) => wsClient.send("game", { action: "set_fog_preview", enabled }))

/* Did anything in this round's battles involve me? The round sound is the ordinary one; a fight on
   my own ground earns the heavier one. Read off the server's own battle map, which is keyed by
   cell — a battle is mine if I am one of the sides in it. */
const battleTouchesMe = (game, state) => {
  const userId = selectUserId(state)
  const battles = game?.state?.battles
  if (!battles || userId == null) return false
  return Object.values(battles).some((battle) =>
    Object.keys(battle?.sides || battle || {}).some((id) => String(id) === String(userId))
  )
}

/* What the round turning sounds (requirement 36, M7). When the message carries a motion log with a
   fight in it, the show plays `battle` on the clash itself, as it is drawn, so the turn only sounds
   `round`; a server that sends no log, or a round with no fight, keeps the two-sound rule. */
export const roundTurnSound = (message, state) =>
  !motionHasClash(message.motion) && battleTouchesMe(message.game, state) ? "battle" : "round"

/* The motion log the round rides on, keyed by the new tick, or null when the server sent none (an
   older server) or nothing happened. */
export const roundMotionOf = (message) =>
  message.motion?.steps?.length ? { tick: message.game.tick, motion: message.motion } : null

/* The viewer's own strike that this payload carries and the board did not: the one just fired, or
   null when the payload is somebody else's shot or a take-back. Pure, for the test. */
export const firedStrikeOf = (before, after, userId) => {
  const own = (metadata) => (metadata?.strikes || []).filter((strike) => String(strike.user_id) === String(userId))
  const held = own(before)
  return own(after).find((strike) => !held.some((h) => String(h.target) === String(strike.target))) ?? null
}

export const thunk = createAsyncThunk("game/startWebSocket", async (data, { dispatch, getState, rejectWithValue }) => {
  // Every game off the socket is the side's view (`zvh/games/broadcast`, 2026-10-17): the
  // events in it are the whole side's, each addressed by `visible_to`, and this keeps mine.
  const mine = (game) => withOnlyMyEvents(game, selectUserId(getState()))
  try {
    // This is a thunk because we need to use the refreshToken from the store, and React doesn't support
    // accessing global store state in reducers. Adn because it's async.
    await wsClient.connect({
      key: "game",
      url: `game/${data.gameId}/`,
      // The server tells the *others* a player connected, not the player: the REST payload this
      // client loaded before connecting still says `connected: false` for itself, so say so here.
      onOpenCallback: [
        () => dispatch(setPlayerPresence({ userId: selectUserId(getState()), connected: true })),
        // ...and the others may be stale too (S2, 2026-09-05). Presence travels as a broadcast to
        // the sockets already in the game's group, so a player whose payload predates the host's
        // connect — the teammate who arrives from the lobby a beat later — kept "disconnected"
        // beside the host until a reload. The socket is open now, so re-read the game once: the
        // REST payload carries every seat's live `connected`, and the map has not moved yet.
        //
        // Not against a local server (basic training, 2026-09-14): there is no REST payload
        // behind it, and `GET /game/` answers with the player's *real* games — an empty list
        // for the new account the tutorial opens for — which emptied the store under the lesson.
        // The view then re-seeded it and remounted, reconnected, re-read: a thousand requests a
        // minute and a city sheet whose inputs remounted before a keystroke landed.
        () => {
          if (!wsClient.isLocal("game")) dispatch(getGames())
        },
        // A tester previewing fog: the server keeps that per connection, so say so on every connect,
        // and again whenever the switch is flipped while this socket is open. Only to a server that
        // has the fog off: one with fog on has nothing to preview and answers every connect with a
        // refusal toast (`FogPreviewUnavailable`) — which is what a browser that kept the switch on
        // from a fog-off session met (2026-10-26). A local server (basic training) has no fog either.
        () => {
          const fogOff = Boolean(getState().common?.gameConfig?.disable_fog_of_war)
          if (!wsClient.isLocal("game") && fogOff && loadFogPreview()) {
            wsClient.send("game", { action: "set_fog_preview", enabled: true })
          }
        },
      ],
      onMessageCallback: [
        (message) => {
          if (message.action === "receive_chat_message") {
            // Everything here is typed by a person. The bots used to speak in the chat too, and
            // say it as a reaction now (owner, 2026-09-09) — see the `reaction` branch below.
            const text = message.message
            if (text) {
              // Somebody else typed (owner, 2026-09-20: "so we are aware"). Not for one's own
              // line, which the server echoes back to the group like everybody's.
              if (message.username && message.username !== selectUsername(getState())) {
                playUiSound("chat")
              }
              dispatch(
                addChatMessage({
                  message: text,
                  userId: message.user_id,
                  username: message.username,
                  messageType: message.message_type,
                })
              )
            }
          } else if (message.action === "reaction") {
            // A bot's line rings when its bubble shows, after the round's show (`ReactionOverlay`).
            if (!message.bot_code) playUiSound("reaction")
            /* A gesture, not a move: no game payload arrives with it and none is asked for. The
               words are this client's own — the server sent an id (`src/utils/reactions.js`) or,
               for a bot saying something about the round that just resolved, a code
               (`src/utils/botLines.js`). One or the other, never both. */
            dispatch(
              addReaction({
                eventId: message.event_id,
                reaction: message.reaction,
                botCode: message.bot_code || null,
                userId: message.user_id,
                username: message.username,
                team: message.team,
              })
            )
          } else if (message.action === "user_set_ready") {
            // Take the server's game rather than patching readiness locally. Readiness decides when
            // the round advances, and a locally-patched copy can drift from what the server thinks.
            if (message.game) {
              dispatch(setGame([mine(message.game)]))
            } else {
              dispatch(setUserReady({ userId: message.user.id }))
            }
          } else if (message.action === "user_unset_ready") {
            // The mirror of set_ready (Q32): end turn is a toggle now. Carries the game for the
            // same reason — the host's RoundHandler re-checks on every game update.
            dispatch(setGame([mine(message.game)]))
          } else if (message.action === "game_next_round") {
            /* The one sound every player hears at the same moment: everybody's orders landing at
               once. A battle on the new board speaks over it, because a round in which somebody
               attacked you is not the same event as a round that merely turned. */
            playUiSound(roundTurnSound(message, getState()))
            /* The round that just turned rides on the same frame (`zvh/games/round_trail`,
               2026-10-17): every order of round N-1 as this viewer may see it, keyed by the new
               tick, for `RoundFlash` to show under "Round N". Set before the board so the two land
               in one picture; the flash clears it, by its round, when the caption goes. It was
               fetched afterwards over HTTP, and in a big game the answer came after the caption had
               already cleared, so the lines of the round before stayed up all round. */
            dispatch(
              setRoundTrail(
                message.trail?.movements?.length ? { tick: message.game.tick, movements: message.trail.movements } : null
              )
            )
            // And the round's motion log (requirement 36), in the same breath: the Game view plays
            // it over the new board. Null when the server sent none, so an older server plays nothing.
            dispatch(setRoundMotion(roundMotionOf(message)))
            // ...and kept beside the board it produced, which is what a look back and a return to
            // the live round play again (`requirements/36` P3).
            const turned = message.motion?.steps?.length
              ? { ...message.game, state: { ...message.game.state, motion: message.motion } }
              : message.game
            dispatch(setGame([mine(turned)]))
          } else if (message.action === "user_leveled_up_building") {
            dispatch(setGame([mine(message.game)]))
          } else if (message.action === "user_enqueued_troops" || message.action === "user_discharged_units") {
            dispatch(setGame([mine(message.game)]))
          } else if (
            message.action === "user_moved_troops" ||
            message.action === "user_loaded_tunnel" ||
            message.action === "user_unloaded_tunnel"
          ) {
            dispatch(setGame([mine(message.game)]))
          } else if (message.action === "user_surrendered") {
            // The payload carries the game, so a finished status arrives with it and the route
            // pin releases on the next render. No separate game_finished action is needed.
            dispatch(setGame([mine(message.game)]))
          } else if (message.action === "game_paused" || message.action === "game_resumed") {
            dispatch(setGame([mine(message.game)]))
            dispatch(
              addChatMessage({
                message: i18n.t(message.action === "game_paused" ? "game.chat.paused" : "game.chat.resumed", {
                  username: message.user.username,
                }),
                messageType: "system",
              })
            )
          } else if (message.action === "formation_set") {
            // Private ack with the game as saved: the board refills from the new priorities.
            dispatch(setGame([mine(message.game)]))
          } else if (message.action === "fog_preview_set") {
            // The game as this connection now sees it — fogged or not — so the map redraws at once.
            dispatch(setGame([mine(message.game)]))
          } else if (message.action === "host_changed") {
            // Carries the game, so RoundHandler sees the new host on the next render and the
            // promoted client starts driving the clock.
            dispatch(setGame([mine(message.game)]))
          } else if (message.action === "user_applied_research") {
            dispatch(setGame([mine(message.game)]))
          } else if (message.action === "user_fired_missile") {
            // The shot is an order now (owner, 2026-10-02): it lands when the round turns, before
            // anybody moves. Say so to whoever fired it, or a missile that did nothing yet reads as
            // a missile that missed. Counted against what we held, since an ally's shot arrives too.
            // `firedStrikeOf` and not a local `mine`: a local of that name shadowed the events
            // helper above and handed `setGame` a list of strikes (owner, 2026-10-23: a runtime
            // error on firing, and on taking the shot back).
            const fired = firedStrikeOf(selectLiveGame(getState())?.metadata, message.game.state, selectUserId(getState()))
            dispatch(setGame([mine(message.game)]))
            if (fired) {
              const [col, row] = fired.target
              dispatch(addMessage({ text: i18n.t("game.strike.scheduled", { where: `${col}-${row}` }), type: "info" }))
            }
          } else if (message.action === "user_founded_settlement") {
            dispatch(setGame([mine(message.game)]))
          } else if (message.action === "user_traded") {
            // Broadcast to the team group only, so receiving this at all means it concerns you —
            // but "concerns you" is not "was sent to you", and the payload used to say only that
            // the game had changed. An ally's gift appeared in the recipient's total looking
            // exactly like a good harvest.
            const before = getState()
            const players = selectGamePlayers(before)
            const said = describeTrade({
              message,
              userId: selectUserId(before),
              usernameFor: (id) =>
                players.find((player) => player.user.id === id)?.user.username ?? i18n.t("game.chat.unknownPlayer", { id }),
              team: selectCurrentTeam(before),
            })
            if (said) dispatch(addMessage({ text: said.text, type: said.type }))
            dispatch(setGame([mine(message.game)]))
          } else if (message.action === "error") {
            // Surface it. These are rules the player just ran into, not internal faults, and they
            // used to be visible only in devtools. A stale round is the one exception: somebody
            // else turned the round this tab was about to, which is the design working.
            if (!isSilentRefusal(message.message)) {
              dispatch(addMessage({ text: playerFacingError(message.message), type: "error" }))
            }
            console.warn("[game ws] server rejected an action:", message.message)
          } else {
            // Unhandled actions used to be dropped in silence, which makes a missing branch look
            // like a backend bug. Every action the backend broadcasts should land above.
            console.warn("[game ws] unhandled action:", message.action)
          }
        },
      ],
      onErrorCallback: [],
      onCloseCallback: [],
    })
    return true
  } catch (error) {
    return rejectWithValue(i18n.t("game.errors.webSocket"))
  }
})

export const onSucceed = null

const thunkObject = { thunk, onSucceed }
export default thunkObject
